import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { mapResponseWithData } from '../helpers/';
import { Producer } from '../models/producer.model';
import { AuthGuardService } from './auth-guard.service';

@Injectable({
    providedIn: 'root'
})
export class ProducerService {

    private storedData: any;
    private currentSubject: BehaviorSubject<Producer>;
    public currentProducer: Observable<Producer>;
    public producers: Producer[];
    private userId: string;
    public organizers: any[];

    constructor(
        private authGuardService: AuthGuardService,
        private http: HttpClient
    ) {
        this.currentSubject = new BehaviorSubject<Producer>(new Producer());
        this.currentProducer = this.currentSubject.asObservable();
    }

    public get currentProducerValue(): Producer {
        return this.currentSubject.value;
    }

    persistData(newData: Producer) {
        this.currentSubject.next(newData);
    }

    getByUserID(user: any): Observable<Producer[]> {
        if (user && (!this.producers || user.UserId !== this.userId)) {
            return this.getProducersInfoByCurrentUser()
                .pipe(
                    map((producers: Producer[]) => {
                        if (producers) {
                            this.userId = user.UserId;
                            this.producers = producers;
                            const expected = new Set();
                            this.organizers = producers.map((producer: Producer) => producer.organizer)
                                .filter(item => !expected.has(JSON.stringify(item)) ? expected.add(JSON.stringify(item)) : false);

                            return producers;
                        } else {
                            return [];
                        }
                    }),
                    catchError(error => throwError(error))
                );
        } else {
            return of(this.producers);
        }
    }

    getFullData(branchOffice, producerNumber, organizerNumber): Observable<any> {
        return this.http.get(environment.server.api.contactManager + '/api/Producers' +
            '/branchOffice/' + branchOffice +
            '/producerNumber/' + producerNumber +
            '/organizerNumber/' + organizerNumber
        ).pipe(map(mapResponseWithData));
    }

    getFormattedProducerCode(producer: Producer | string): string {

        let producerNumber: string;
        let branchOfficeNumber: string;

        if (typeof (producer) === 'object') {
            producerNumber = String(producer.number).padStart(6, '0');
            branchOfficeNumber = String(producer.branchOffice).padStart(2, '0');
        } else if (typeof (producer) === 'string') {
            branchOfficeNumber = String(producer.split('-')[0]).padStart(2, '0');
            producerNumber = String(producer.split('-')[1]).padStart(6, '0');
        }
        return `${branchOfficeNumber}-${producerNumber}`;
    }

    getUsersByProducerCode(userCode) {
        return this.http.get(environment.server.api.auth + '/api/user?producerCode=' + userCode);
    }

    getProducerByProducerCode(producerCode: string): Observable<Producer> {
        const producerCodeParts = producerCode.split('-');
        const branchOffice = Number(producerCodeParts[0]);
        const producerNumber = Number(producerCodeParts[1]);

        return this.getByUserID(this.authGuardService.user).pipe(
            map(producers => {
                return producers.find(p => p.number === producerNumber && p.branchOffice === branchOffice);
            })
        );
    }

    getProducerCodes(): Observable<string[]> {
        return this.getByUserID(this.authGuardService.user).pipe(map(producers => {
            return producers ? producers.map(producer => this.getFormattedProducerCode(producer)) : [];
        }));
    }

    getMicrositeProducerInformation(cuit) {
        return this.http.get(`${environment.server.api.marketing}/Microsites/MyData/${cuit}`)
    }

    updateQuoteStatus(producerCodes) {
        const body = {
            producerCodes: producerCodes
        }
        return this.http.put(`${environment.server.api.marketing}/Quote/UpdateQuoteStatus`, body);
    }

    getProducersInfoByCurrentUser(withCommissionValidation: boolean = false): Observable<Producer[]> {

        return this.http.get<Producer[]>(`${environment.server.api.portalpas}/Producer?withCommissionValidation=${withCommissionValidation}`)
            .pipe(map(producers => {
                producers.forEach(producer => {
                    const producerCodeParts = producer.code.split('-');
                    producer.branchOffice = +producerCodeParts[0];
                    producer.number = +producerCodeParts[1];
                    const organizerCodeParts = producer.organizer.code.split('-');
                    producer.organizerNumber = +organizerCodeParts[1];
                    producer.organizer.branchOffice = +organizerCodeParts[0];
                    producer.organizer.number = +organizerCodeParts[1];
                });
                return producers.sort((a, b) => a.number < b.number ? -1 : 1);
            }));

    }

    producerByProducerCode(producerCode: string) {
        return this.http.get(`${environment.server.api.portalpasProxy}/policycenter-producer/api/v3.1/Producers/${producerCode}`);
    }
    getLoggedInProducerWithCuit(cuitWithOutLastDigit: string) {
        const searchByCuit = this.producers.find(producer => producer.cuit.replace(/[-]*/g, '').startsWith(cuitWithOutLastDigit))
            || this.organizers.find(organizer => organizer.cuit.replace(/[-]*/g, '').startsWith(cuitWithOutLastDigit));
        const searchByNumber = this.producers.find(producer => cuitWithOutLastDigit.endsWith(producer.number.toString()))
            || this.organizers.find(producer => cuitWithOutLastDigit.endsWith(producer.number.toString()));
        return searchByCuit ? searchByCuit : searchByNumber;
    }

    getPersonsByDocNumberV3(doumentId: string, officialIdTypeCode: string): Observable<any> {
        const baseUrl = environment.server.api.contactManager;

        return this.http.get(`${baseUrl}/api/Contacts/SearchByTaxId?taxId=${doumentId}&officialIdTypeCode=${officialIdTypeCode}`);
    }
}
